<template>
    <div class="email-editor" :class="{'is-disabled': !editable, 'has-appendix': !!appendixSrc}" @keyup="$emit('keyup')" @paste="$emit('keyup')" @dblclick="$emit('dblclick', $event)">
        <!-- keys: Toolbar und Verlauf erscheinen erst nach dem Mounten – ohne key verschiebt Vue sonst den
             Inhaltsbereich samt Shadow DOM in die Toolbar (Antworten auf rstours) -->
        <div v-if="editor && editable" key="toolbar" class="email-editor-toolbar" @mousedown="keepSelection">
            <div class="email-editor-group">
                <div class="select is-small email-editor-font">
                    <select :value="fontFamily" title="Schriftart" @change="setFontFamily($event.target.value)">
                        <option value="">{{ houseFontLabel }}</option>
                        <option v-for="font in fontFamilyOptions" :key="font.label" :value="font.label">{{ font.label }}</option>
                    </select>
                </div>
                <div class="select is-small">
                    <select :value="fontSize" title="Schriftgrad" @change="setFontSize($event.target.value)">
                        <option v-for="size in fontSizes" :key="size" :value="size">{{ size.replace('pt', '') }}</option>
                    </select>
                </div>
                <button type="button" title="Schriftgrad vergrößern" @click="stepFontSize(1)">A<sup>+</sup></button>
                <button type="button" title="Schriftgrad verkleinern" @click="stepFontSize(-1)">A<sup>−</sup></button>
            </div>

            <div class="email-editor-group">
                <button type="button" title="Fett (Strg+B)" :class="{'is-active': is('bold')}" @click="run(c => c.toggleBold(), 'bold')"><i class="material-icons">format_bold</i></button>
                <button type="button" title="Kursiv (Strg+I)" :class="{'is-active': is('italic')}" @click="run(c => c.toggleItalic(), 'italic')"><i class="material-icons">format_italic</i></button>
                <button type="button" title="Unterstrichen (Strg+U)" :class="{'is-active': is('underline')}" @click="run(c => c.toggleUnderline(), 'underline')"><i class="material-icons">format_underlined</i></button>
                <button type="button" title="Durchgestrichen" :class="{'is-active': is('strike')}" @click="run(c => c.toggleStrike(), 'strikeThrough')"><i class="material-icons">format_strikethrough</i></button>
                <div class="email-editor-palette-wrapper">
                    <button type="button" title="Texthervorhebungsfarbe" @click="togglePalette('highlight')">
                        <i class="material-icons">border_color</i><span class="email-editor-swatch-bar" :style="{background: lastHighlight}"></span>
                    </button>
                    <div v-if="palette === 'highlight'" class="email-editor-palette">
                        <button v-for="color in highlightColors" :key="color" type="button" class="email-editor-swatch" :style="{background: color}" :title="color" @click="setHighlight(color)"></button>
                        <button type="button" class="email-editor-palette-reset" @click="setHighlight(null)">Keine Farbe</button>
                    </div>
                </div>
                <div class="email-editor-palette-wrapper">
                    <button type="button" title="Schriftfarbe" @click="togglePalette('color')">
                        <i class="material-icons">format_color_text</i><span class="email-editor-swatch-bar" :style="{background: lastColor}"></span>
                    </button>
                    <div v-if="palette === 'color'" class="email-editor-palette">
                        <button v-for="color in textColors" :key="color" type="button" class="email-editor-swatch" :style="{background: color}" :title="color" @click="setColor(color)"></button>
                        <button type="button" class="email-editor-palette-reset" @click="setColor(null)">Automatisch</button>
                    </div>
                </div>
            </div>

            <div class="email-editor-group">
                <button type="button" title="Aufzählungszeichen" :class="{'is-active': is('bulletList')}" @click="run(c => c.toggleBulletList(), 'insertUnorderedList')"><i class="material-icons">format_list_bulleted</i></button>
                <button type="button" title="Nummerierung" :class="{'is-active': is('orderedList')}" @click="run(c => c.toggleOrderedList(), 'insertOrderedList')"><i class="material-icons">format_list_numbered</i></button>
                <button type="button" title="Einzug verkleinern" @click="run(c => c.outdent(), 'outdent')"><i class="material-icons">format_indent_decrease</i></button>
                <button type="button" title="Einzug vergrößern" @click="run(c => c.indent(), 'indent')"><i class="material-icons">format_indent_increase</i></button>
            </div>

            <div class="email-editor-group">
                <button v-for="align in alignments" :key="align.value" type="button" :title="align.title"
                        :class="{'is-active': isAlign(align.value)}" @click="run(c => c.setTextAlign(align.value), align.native)">
                    <i class="material-icons">{{ align.icon }}</i>
                </button>
            </div>

            <div class="email-editor-group">
                <button type="button" title="Link einfügen" :class="{'is-active': is('link')}" @click="setLink"><i class="material-icons">link</i></button>
                <button type="button" title="Tabelle einfügen" @click="insertTable"><i class="material-icons">table_chart</i></button>
                <button type="button" title="Alle Formatierungen löschen" @click="clearFormatting"><i class="material-icons">format_clear</i></button>
            </div>

            <div class="email-editor-group">
                <button type="button" title="Rückgängig (Strg+Z)" :disabled="!blockCell && !can(c => c.undo())" @click="run(c => c.undo(), 'undo')"><i class="material-icons">undo</i></button>
                <button type="button" title="Wiederholen (Strg+Y)" :disabled="!blockCell && !can(c => c.redo())" @click="run(c => c.redo(), 'redo')"><i class="material-icons">redo</i></button>
            </div>

            <!-- Kontextzeile: immer da (Editor springt nicht), zeigt Werkzeuge für das, worin der Cursor steht -->
            <div class="email-editor-context">
            <div v-if="blockCell" class="email-editor-group is-table-tools">
                <span class="email-editor-group-label">Tabelle</span>
                <button type="button" title="Zeile oberhalb einfügen (Stil wird übernommen)" @click="changeBlock('rowBefore')"><i class="material-icons">table_rows</i><i class="material-icons">arrow_upward</i></button>
                <button type="button" title="Zeile unterhalb einfügen (Stil wird übernommen)" @click="changeBlock('rowAfter')"><i class="material-icons">table_rows</i><i class="material-icons">arrow_downward</i></button>
                <button type="button" title="Zeile löschen" @click="changeBlock('rowDelete')"><i class="material-icons">table_rows</i><i class="material-icons">close</i></button>
                <button type="button" title="Spalte links einfügen (Breite wird geteilt)" @click="changeBlock('columnBefore')"><i class="material-icons">view_column</i><i class="material-icons">arrow_back</i></button>
                <button type="button" title="Spalte rechts einfügen (Breite wird geteilt)" @click="changeBlock('columnAfter')"><i class="material-icons">view_column</i><i class="material-icons">arrow_forward</i></button>
                <button type="button" title="Spalte löschen (Breite geht an die Nachbarspalte)" @click="changeBlock('columnDelete')"><i class="material-icons">view_column</i><i class="material-icons">close</i></button>
                <div class="email-editor-palette-wrapper">
                    <button type="button" title="Zellenfarbe" @click="togglePalette('cell')"><i class="material-icons">format_color_fill</i></button>
                    <div v-if="palette === 'cell'" class="email-editor-palette">
                        <button v-for="color in cellColors" :key="color" type="button" class="email-editor-swatch" :style="{background: color}" :title="color" @click="changeBlock('cellColor', color)"></button>
                        <button type="button" class="email-editor-palette-reset" @click="changeBlock('cellColor', null)">Keine Farbe</button>
                    </div>
                </div>
                <button type="button" title="Tabelle löschen" @click="deleteBlockTable"><i class="material-icons">delete</i></button>
            </div>
            </div>

        </div>

        <div key="body" class="email-editor-body" ref="body">
            <!-- Inhalt im Shadow DOM (contentStyle.js): Travelize-CSS erreicht die Mail nicht. Mit Signatur wächst der
                 Editor mit dem Text, die Signatur folgt direkt darauf wie in der Mail -->
            <div key="contentHost" ref="contentHost" class="email-editor-content-host" :class="{'has-appendix': !!appendixSrc, 'is-disabled': !editable}"
                 :style="minHeight && !appendixSrc ? {minHeight: minHeight + 'px'} : null"></div>

            <div v-if="uploading" class="email-editor-uploading">Bild wird hochgeladen …</div>

            <div v-if="pasted && editable" class="email-editor-paste-options" :style="{top: pasted.top + 'px', left: pasted.left + 'px'}" @mousedown.prevent>
                <button type="button" title="Einfügeoptionen" @click="pasteMenuOpen = !pasteMenuOpen">
                    <i class="material-icons">content_paste</i><i class="material-icons">arrow_drop_down</i>
                </button>
                <div v-if="pasteMenuOpen" class="email-editor-paste-menu">
                    <button v-for="option in pasteOptions" :key="option.mode" type="button"
                            :class="{'is-active': pasted.mode === option.mode}" @click="applyPasteMode(option.mode)">
                        <i class="material-icons">{{ option.icon }}</i>{{ option.label }}
                    </button>
                </div>
            </div>
        </div>

        <div v-if="appendixSrc" key="appendix" class="email-editor-appendix" :class="{'is-editing': signatureEditing}">
            <div v-if="editable && appendixEditLabel" class="email-editor-appendix-bar">
                <button type="button" class="button is-small" @click="toggleSignatureEditing">
                    <i class="material-icons">{{ signatureEditing ? 'check' : 'edit' }}</i>
                    <span>{{ signatureEditing ? 'Fertig' : appendixEditLabel }}</span>
                </button>
                <span v-if="signatureEditing" class="email-editor-appendix-hint">Änderungen gelten nur für diese Email.</span>
            </div>
            <iframe ref="appendix" sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox" :srcdoc="appendixSrc" :style="{height: appendixHeight + 'px'}" @load="onAppendixLoad"></iframe>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import { Editor } from '@tiptap/core';
import contentStyle from './contentStyle';
import { notifyError } from '@components/Notification';
import emailExtensions from './extensions';
import cleanPastedHtml, { stripTrailingBreaks } from './pasteCleaner';
import { dataUriToBlob } from './clipboardImages';
import { addColumn, addRow, deleteColumn, deleteRow, newTableHtml, setCellBackground } from './templateTable';
import debounce from 'lodash/debounce';
import { assembleEmailDocument, documentWithoutBody, partsFromEditedAppendix, QUOTE_ATTRIBUTE, quoteIn, serializeEmailBody, signatureIn, splitEmailDocument } from './emailHtml';
import { DEFAULT_FONT_SIZE, FONT_FAMILIES, FONT_FAMILY, FONT_SIZES, HIGHLIGHT_COLORS, HOUSE_FONT_LABEL, TABLE_HEADER_BACKGROUND, TEXT_COLORS, isHouseFont, normalizeFontSize, primaryFont } from './houseStyle';

// Zustand der Leiste, wenn der Cursor in einer Tabelle steht (document.queryCommandState)
const NATIVE_STATE = {bold: 'bold', italic: 'italic', underline: 'underline', strike: 'strikeThrough', bulletList: 'insertUnorderedList', orderedList: 'insertOrderedList'};

// Email-Editor im Stil des Outlook-Desktop-Editors.
// v-model ist das komplette Mail-Dokument (TopPart + Text + Signatur + Verlauf, siehe clientSpecific/email/template):
// bearbeitet wird nur der Text zwischen <!-- bodyContentStart/End -->, der Rest wird unverändert wieder angehängt
// und schreibgeschützt darunter angezeigt. Ohne Marker (z.B. Telefonnotiz) ist v-model nur der Text.
export default {
    props: {
        value: {
            type: String,
            default: '',
        },
        minHeight: {
            type: Number,
            default: 300,
        },
        placeholder: {
            type: String,
            default: '',
        },
        editable: {
            type: Boolean,
            default: true,
        },
    },

    data() {
        return {
            editor: null,
            parts: null,
            lastEmitted: null,
            lastBody: null,
            appendixHeight: 0,
            appendixSrc: '',
            signatureEditing: false,
            palette: null,
            lastColor: '#C00000',
            lastHighlight: '#FFFF00',
            tick: 0,
            pasted: null,
            blockCell: null,
            pasteMenuOpen: false,
            uploading: 0,
            uploads: {},
            pasteOptions: [
                {mode: 'keep', icon: 'format_paint', label: 'Ursprüngliche Formatierung beibehalten'},
                {mode: 'merge', icon: 'merge_type', label: 'Formatierung zusammenführen'},
                {mode: 'text', icon: 'text_fields', label: 'Nur den Text übernehmen'},
            ],
            fontSizes: FONT_SIZES,
            houseFontLabel: HOUSE_FONT_LABEL,
            textColors: TEXT_COLORS,
            highlightColors: HIGHLIGHT_COLORS,
            cellColors: [TABLE_HEADER_BACKGROUND, ...HIGHLIGHT_COLORS],
            alignments: [
                {value: 'left', native: 'justifyLeft', icon: 'format_align_left', title: 'Linksbündig'},
                {value: 'center', native: 'justifyCenter', icon: 'format_align_center', title: 'Zentriert'},
                {value: 'right', native: 'justifyRight', icon: 'format_align_right', title: 'Rechtsbündig'},
                {value: 'justify', native: 'justifyFull', icon: 'format_align_justify', title: 'Blocksatz'},
            ],
        };
    },

    computed: {
        // Vorlagen ohne Signatur (z.B. rstours: Signatur steht im Text) brauchen keinen "Signatur bearbeiten"-Knopf
        hasSignature() {
            return !!this.parts && signatureIn(this.parts);
        },

        // weitergeleitete/beantwortete Mail darunter: lässt sich ebenfalls bearbeiten (z.B. Weitergeleitetes rauslöschen)
        appendixEditLabel() {
            const quote = !!this.parts && quoteIn(this.parts);
            if (this.hasSignature && quote) {
                return 'Signatur/Verlauf bearbeiten';
            }
            return this.hasSignature ? 'Signatur bearbeiten' : (quote ? 'Verlauf bearbeiten' : '');
        },

        // angezeigte Schrift: Name der ersten Schrift; '' = Hausschrift (Open Sans)
        fontFamily() {
            this.tick;
            let family = '';
            if (this.blockCell) {
                const element = this.blockSelectionElement();
                family = element ? window.getComputedStyle(element).fontFamily : '';
            } else if (this.editor) {
                family = this.editor.getAttributes('textStyle').fontFamily || '';
            }
            return isHouseFont(family) ? '' : primaryFont(family);
        },

        // eingefügte Schrift, die nicht in der Liste steht, trotzdem anzeigen
        fontFamilyOptions() {
            const current = this.fontFamily;
            return current && !FONT_FAMILIES.some(font => font.label.toLowerCase() === current.toLowerCase())
                ? [...FONT_FAMILIES, {label: current, value: current}]
                : FONT_FAMILIES;
        },

        fontSize() {
            this.tick;
            if (this.blockCell) {
                const element = this.blockSelectionElement();
                const px = element ? parseFloat(window.getComputedStyle(element).fontSize) : NaN;
                return (px && normalizeFontSize(px + 'px')) || DEFAULT_FONT_SIZE;
            }
            return (this.editor && this.editor.getAttributes('textStyle').fontSize) || DEFAULT_FONT_SIZE;
        },
    },

    methods: {
        is(name, attributes) {
            this.tick;
            if (this.blockCell) {
                return NATIVE_STATE[name] ? this.nativeState(NATIVE_STATE[name]) : false;
            }
            return !!this.editor && this.editor.isActive(name, attributes);
        },

        isAlign(value) {
            this.tick;
            if (this.blockCell) {
                return this.nativeState(this.alignments.find(align => align.value === value).native);
            }
            return !!this.editor && this.editor.isActive({textAlign: value});
        },

        nativeState(command) {
            try {
                return document.queryCommandState(command);
            } catch (e) {
                return false;
            }
        },

        // Cursor steht in einer Tabelle (Shadow DOM, HtmlBlock): die Leiste formatiert dort direkt die Markierung
        nativeCommand(command, value = null) {
            document.execCommand('styleWithCSS', false, true);
            document.execCommand(command, false, value);
            this.palette = null;
            this.tick++;
        },

        blockSelectionElement() {
            const host = this.blockCell && this.blockCell.getRootNode().host;
            const selection = host && host.shadowRoot.getSelection ? host.shadowRoot.getSelection() : window.getSelection();
            const node = selection && selection.anchorNode;
            return node && (node.nodeType === 1 ? node : node.parentElement);
        },

        blockFontSize(size) {
            const inner = this.blockCell.closest('.email-html-block-inner');
            this.nativeCommand('fontSize', '7');
            inner.querySelectorAll('span[style*="xxx-large"], font[size="7"]').forEach(element => {
                if (element.tagName === 'FONT') {
                    element.removeAttribute('size');
                }
                element.style.fontSize = size;
            });
            inner.dispatchEvent(new Event('input'));
        },

        can(command) {
            this.tick;
            return !!this.editor && command(this.editor.can().chain()).run();
        },

        run(command, native) {
            if (this.blockCell && native) {
                this.nativeCommand(native);
                return;
            }
            command(this.editor.chain().focus()).run();
            this.palette = null;
        },

        // Klick in die Leiste soll die Auswahl im Text nicht aufheben (select braucht den Fokus)
        keepSelection(event) {
            if (event.target.tagName !== 'SELECT' && event.target.tagName !== 'OPTION') {
                event.preventDefault();
            }
        },

        togglePalette(name) {
            this.palette = this.palette === name ? null : name;
        },

        setFontFamily(label) {
            const font = this.fontFamilyOptions.find(option => option.label === label);
            if (this.blockCell) {
                this.nativeCommand('fontName', font ? font.value : FONT_FAMILY);
                return;
            }
            this.run(c => font ? c.setFontFamily(font.value) : c.unsetFontFamily());
        },

        setFontSize(size) {
            if (this.blockCell) {
                this.blockFontSize(size);
                return;
            }
            this.run(c => size === DEFAULT_FONT_SIZE ? c.unsetFontSize() : c.setFontSize(size));
        },

        stepFontSize(direction) {
            const index = FONT_SIZES.indexOf(this.fontSize);
            const next = FONT_SIZES[Math.max(0, Math.min(FONT_SIZES.length - 1, (index < 0 ? FONT_SIZES.indexOf(DEFAULT_FONT_SIZE) : index) + direction))];
            this.setFontSize(next);
        },

        setColor(color) {
            if (color) {
                this.lastColor = color;
            }
            if (this.blockCell) {
                this.nativeCommand('foreColor', color || '#000000');
                return;
            }
            this.run(c => color ? c.setColor(color) : c.unsetColor());
        },

        setHighlight(color) {
            if (color) {
                this.lastHighlight = color;
            }
            if (this.blockCell) {
                this.nativeCommand('hiliteColor', color || 'transparent');
                return;
            }
            this.run(c => color ? c.setBackgroundColor(color) : c.unsetBackgroundColor());
        },

        clearFormatting() {
            if (this.blockCell) {
                this.nativeCommand('removeFormat');
                return;
            }
            this.run(c => c.unsetMark('bold').unsetMark('italic').unsetMark('underline').unsetMark('strike')
                .unsetMark('textStyle').unsetTextAlign().updateAttributes('paragraph', {indent: 0}));
        },

        setLink() {
            const previousUrl = this.blockCell ? null : this.editor.getAttributes('link').href;
            const url = window.prompt('Adresse', previousUrl || 'https://');
            if (url === null) {
                return;
            }
            if (url === '' || url === 'https://') {
                if (this.blockCell) {
                    this.nativeCommand('unlink');
                    return;
                }
                this.run(c => c.extendMarkRange('link').unsetLink());
                return;
            }
            const href = /^(https?:|mailto:|tel:)/i.test(url) ? url : (url.includes('@') ? 'mailto:' + url : 'https://' + url);
            if (this.blockCell) {
                this.nativeCommand('createLink', href);
                return;
            }
            if (this.editor.state.selection.empty && !this.editor.isActive('link')) {
                this.run(c => c.insertContent({type: 'text', text: url, marks: [{type: 'link', attrs: {href}}]}));
            } else {
                this.run(c => c.extendMarkRange('link').setLink({href}));
            }
        },

        // Einfügeoptionen wie in Word/Outlook: Symbol unter dem eingefügten Text, Standard = zusammenführen
        onPasted({from, to, clipboard}) {
            const coords = this.editor.view.coordsAtPos(Math.min(to, this.editor.state.doc.content.size));
            const box = this.$refs.body.getBoundingClientRect();
            this.pasted = {from, to, clipboard, mode: 'merge', top: coords.bottom - box.top + 2, left: Math.max(0, coords.left - box.left)};
            this.pasteMenuOpen = false;
        },

        pasteHtml(mode) {
            const clipboard = this.pasted.clipboard;
            if (mode === 'text') {
                const text = clipboard.text || new DOMParser().parseFromString(clipboard.resolvedHtml || '', 'text/html').body.textContent;
                return text.split(/\r?\n/).map(line => `<p>${line.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>`).join('');
            }
            return cleanPastedHtml(clipboard.resolvedHtml || clipboard.html || '', {keepFormatting: mode === 'keep'});
        },

        applyPasteMode(mode) {
            const {from, to} = this.pasted;
            const html = this.pasteHtml(mode);
            this.editor.chain().focus()
                .command(({tr}) => {
                    tr.setMeta('emailPasteOption', true);
                    return true;
                })
                // tiptap behält bei insertContentAt sonst jeden Leerraum (Umbrüche zwischen <li> → leere Aufzählungspunkte)
                .insertContentAt({from, to}, html || '', {parseOptions: {preserveWhitespace: false}})
                .run();
            const end = this.editor.state.selection.to;
            this.onPasted({from, to: end, clipboard: this.pasted.clipboard});
            this.pasted.mode = mode;
        },

        onTransaction({transaction}) {
            this.tick++;
            if (this.pasted && transaction.docChanged && transaction.getMeta('uiEvent') !== 'paste'
                && !transaction.getMeta('emailPasteOption') && !transaction.getMeta('imageUpload')) {
                this.pasted = null;
            }
        },

        // data:-Bilder (eingefügt) hochladen; Outlook zeigt nur Bilder, die als Inline-Anhang (cid:) mitkommen
        uploadPendingImages() {
            const sources = new Set();
            this.editor.state.doc.descendants(node => {
                if (node.type.name === 'image' && /^data:image\//.test(node.attrs.src || '') && !this.uploads[node.attrs.src]) {
                    sources.add(node.attrs.src);
                }
                if (node.type.name === 'htmlBlock') {
                    (node.attrs.html.match(/data:image\/(?:png|jpe?g|gif);base64,[A-Za-z0-9+/=]+/g) || [])
                        .filter(source => !this.uploads[source])
                        .forEach(source => sources.add(source));
                }
            });
            sources.forEach(source => {
                this.uploads[source] = true;
                this.uploading++;
                const form = new FormData();
                const blob = dataUriToBlob(source);
                form.append('file', blob, 'bild.' + (blob.type.split('/')[1] || 'png'));
                axios.post('/api/emails/inline_image', form)
                    .then(response => this.replaceImageSource(source, response.data.src))
                    .catch(error => {
                        notifyError('Bild konnte nicht hochgeladen werden: ' + ((error.response && error.response.data && error.response.data.error) || error.message));
                    })
                    .finally(() => {
                        this.uploading--;
                    });
            });
        },

        replaceImageSource(source, target) {
            if (!this.editor || this.editor.isDestroyed) {
                return;
            }
            const tr = this.editor.state.tr;
            this.editor.state.doc.descendants((node, pos) => {
                if (node.type.name === 'image' && node.attrs.src === source) {
                    tr.setNodeMarkup(pos, undefined, {...node.attrs, src: target});
                }
                if (node.type.name === 'htmlBlock' && node.attrs.html.includes(source)) {
                    tr.setNodeMarkup(pos, undefined, {...node.attrs, html: node.attrs.html.split(source).join(target)});
                }
            });
            tr.setMeta('addToHistory', false).setMeta('imageUpload', true);
            this.editor.view.dispatch(tr);
            if (this.pasted && this.pasted.clipboard.resolvedHtml) {
                this.pasted.clipboard.resolvedHtml = this.pasted.clipboard.resolvedHtml.split(source).join(target);
            }
        },

        // Cursor in einer Baustein-Tabelle (HtmlBlock)? Dort arbeitet der Browser direkt im HTML
        onSelectionChange() {
            // Fokus in einer Tabelle: Editor-Inhalt und Tabelle liegen je in einem Shadow DOM – activeElement durchsteigen
            let host = null;
            let active = document.activeElement;
            while (active) {
                if (active.classList && active.classList.contains('email-html-block')) {
                    host = active;
                }
                active = active.shadowRoot ? active.shadowRoot.activeElement : null;
            }
            if (!host || !this.contentRoot || !this.contentRoot.contains(host) || !host.shadowRoot) {
                this.blockCell = null;
                return;
            }
            const selection = host.shadowRoot.getSelection ? host.shadowRoot.getSelection() : window.getSelection();
            const node = selection && selection.anchorNode;
            const element = node && (node.nodeType === 1 ? node : node.parentElement);
            this.blockCell = element && element.closest ? element.closest('td, th') : null;
            this.tick++;
        },

        insertTable() {
            this.run(c => c.insertContent({type: 'htmlBlock', attrs: {html: newTableHtml()}}));
        },

        // Tabelle (HtmlBlock), in der der Cursor steht, als Ganzes löschen
        deleteBlockTable() {
            const host = this.blockCell && this.blockCell.getRootNode().host;
            let target = null;
            this.editor.state.doc.descendants((node, pos) => {
                if (!target && node.type.name === 'htmlBlock' && this.editor.view.nodeDOM(pos) === host) {
                    target = {pos, size: node.nodeSize};
                }
                return !target;
            });
            if (target) {
                this.editor.chain().focus().deleteRange({from: target.pos, to: target.pos + target.size}).run();
            }
            this.blockCell = null;
            this.palette = null;
        },

        changeBlock(action, color) {
            const cell = this.blockCell;
            const inner = cell && cell.closest('.email-html-block-inner');
            if (!inner) {
                return;
            }
            const actions = {
                rowBefore: () => addRow(cell.parentElement, 'before').cells[0],
                rowAfter: () => addRow(cell.parentElement, 'after').cells[0],
                rowDelete: () => {
                    const next = deleteRow(cell.parentElement);
                    return next && next.cells ? next.cells[0] : null;
                },
                columnBefore: () => addColumn(cell, 'before'),
                columnAfter: () => addColumn(cell, 'after'),
                columnDelete: () => deleteColumn(cell),
                cellColor: () => {
                    setCellBackground(cell, color);
                    return cell;
                },
            };
            const focus = actions[action]();
            if (focus && focus.isConnected) {
                const range = document.createRange();
                range.selectNodeContents(focus.firstElementChild || focus);
                const selection = window.getSelection();
                selection.removeAllRanges();
                selection.addRange(range);
            }
            this.blockCell = focus && focus.isConnected ? focus : null;
            this.palette = null;
            inner.dispatchEvent(new Event('input'));
        },

        loadValue(value) {
            this.parts = splitEmailDocument(value);
            const body = this.parts ? this.parts.body : (value || '');
            this.editor.commands.setContent(stripTrailingBreaks(body), {emitUpdate: false});
            this.pasted = null;
            this.lastEmitted = value;
            this.lastBody = serializeEmailBody(this.editor.getHTML());
        },

        emitValue() {
            const body = serializeEmailBody(this.editor.getHTML());
            this.lastBody = body;
            this.lastEmitted = this.parts ? assembleEmailDocument(this.parts, body) : body;
            this.$emit('input', this.lastEmitted);
            this.uploadPendingImages();
        },

        // ProseMirror hört auf mouseup nur in seinem Shadow DOM (view.root). Beim Markieren außerhalb losgelassen
        // (Vorlage-Feld darüber, Verlauf darunter) blieb es im Markier-Modus und setzte den sichtbaren Cursor nicht
        // mehr – Enter wirkte, der Cursor blieb stehen. Bubble-Phase: Klicks im Editor hat ProseMirror da schon erledigt.
        endMouseSelection(event) {
            const pending = this.editor && this.editor.view.input.mouseDown;
            if (pending) {
                pending.up(event);
            }
        },

        onAppendixLoad() {
            // Links in Signatur/Verlauf öffnen einen neuen Tab (nur Anzeige, wird beim Bearbeiten wieder entfernt)
            const doc = this.$refs.appendix && this.$refs.appendix.contentDocument;
            if (doc) {
                doc.addEventListener('mouseup', this.endMouseSelection);
            }
            if (doc && doc.head && !doc.querySelector('base[data-email-preview]')) {
                const base = doc.createElement('base');
                base.setAttribute('target', '_blank');
                base.setAttribute('data-email-preview', '1');
                doc.head.prepend(base);
            }
            this.resizeAppendix();
            if (this.signatureEditing) {
                this.startSignatureEditing();
            }
        },

        toggleSignatureEditing() {
            if (!this.signatureEditing) {
                this.signatureEditing = true;
                this.startSignatureEditing();
                return;
            }
            // "Fertig": letzte Änderungen (noch im Debounce) übernehmen, bevor die Vorschau neu geladen wird
            this.appendixInput.cancel();
            this.commitAppendix();
            this.signatureEditing = false;
            const doc = this.$refs.appendix && this.$refs.appendix.contentDocument;
            if (doc) {
                doc.removeEventListener('input', this.appendixInput);
                doc.designMode = 'off';
            }
            this.appendixSrc = this.parts ? documentWithoutBody(this.parts) : '';
        },

        // Signatur und Verlauf direkt im Vorschau-Dokument bearbeiten; außerhalb davon ist der Verlauf gesperrt
        // (contenteditable=false aus documentWithoutBody)
        startSignatureEditing() {
            const doc = this.$refs.appendix && this.$refs.appendix.contentDocument;
            if (!doc || doc.designMode === 'on') {
                return;
            }
            doc.querySelectorAll(`[${QUOTE_ATTRIBUTE}]`).forEach(quote => quote.removeAttribute('contenteditable'));
            doc.designMode = 'on';
            doc.addEventListener('input', this.appendixInput);
            doc.body.focus();
        },

        // bearbeitete Vorschau in die Mail übernehmen; nur solange bearbeitet wird (danach lädt die Vorschau neu)
        commitAppendix() {
            const doc = this.$refs.appendix && this.$refs.appendix.contentDocument;
            if (!this.signatureEditing || !doc || doc.designMode !== 'on' || !this.parts) {
                return;
            }
            const parts = partsFromEditedAppendix(doc.documentElement, this.parts.body);
            if (!parts) {
                // unsichtbare Text-Marken mitgelöscht (z.B. Strg+A, Entf): nicht still verwerfen, Vorschau zurücksetzen
                notifyError('Signatur/Verlauf: Diese Änderung lässt sich nicht übernehmen (alles markiert und gelöscht?). Bitte nur Teile markieren.');
                // abweichender srcdoc erzwingt das Neuladen, auch wenn sich parts seit Bearbeitungsbeginn nicht geändert hat
                this.appendixSrc = documentWithoutBody(this.parts) + (this.appendixSrc.endsWith(' ') ? '' : ' ');
                return;
            }
            this.parts = parts;
            this.emitValue();
            this.$emit('keyup');
            this.resizeAppendix();
        },

        resizeAppendix() {
            const frame = this.$refs.appendix;
            const doc = frame && frame.contentDocument;
            if (doc && doc.body) {
                this.alignAppendix(doc);
                this.appendixHeight = Math.max(doc.documentElement.scrollHeight, doc.body.scrollHeight);
            }
        },

        // Vorschau dort beginnen lassen, wo in der Mail der Text steht (Marker bodyContentStart), und links bündig
        // zum Editor-Text: dann ist der Abstand Text → Signatur genau wie beim Empfänger
        alignAppendix(doc) {
            if (doc.querySelector('style[data-email-preview]')) {
                return;
            }
            const walker = doc.createTreeWalker(doc.body, 128 /* NodeFilter.SHOW_COMMENT */);
            let marker = null;
            while (walker.nextNode()) {
                if (walker.currentNode.data.trim() === 'bodyContentStart') {
                    marker = walker.currentNode;
                    break;
                }
            }
            const anchor = marker && marker.parentElement;
            const content = this.contentRoot && this.contentRoot.querySelector('.ProseMirror');
            if (!anchor || !content) {
                return;
            }
            const style = doc.defaultView.getComputedStyle(doc.body);
            const box = anchor.getBoundingClientRect();
            const textLeft = parseFloat(window.getComputedStyle(content).paddingLeft) || 0;
            // als eigenes <style data-email-preview>, nicht am <body>: der bearbeitete Verlauf wird samt <body>
            // zurück in die Mail geschrieben – Ränder am <body> verschoben sie beim Empfänger
            const alignment = doc.createElement('style');
            alignment.setAttribute('data-email-preview', '1');
            alignment.textContent = `body{margin-top:${parseFloat(style.marginTop) - box.top}px !important;`
                + `margin-left:${parseFloat(style.marginLeft) - (box.left - textLeft)}px !important}`;
            doc.head.appendChild(alignment);
        },

        closePalette(event) {
            if (this.palette && !event.target.closest('.email-editor-palette-wrapper')) {
                this.palette = null;
            }
            if (this.pasteMenuOpen && !event.target.closest('.email-editor-paste-options')) {
                this.pasteMenuOpen = false;
            }
        },
    },

    watch: {
        // Vorschau nur neu laden, wenn nicht gerade die Signatur bearbeitet wird (sonst springt der Cursor)
        parts(parts) {
            if (!this.signatureEditing) {
                this.appendixSrc = parts ? documentWithoutBody(parts) : '';
            }
        },

        value(value) {
            if (value === this.lastEmitted) {
                return;
            }
            // Neu gerendert (Vorschau/Twig) bei gleichem Text: nur Signatur/Verlauf übernehmen, Cursor bleibt
            const parts = splitEmailDocument(value);
            if (parts && this.parts && serializeEmailBody(parts.body) === this.lastBody) {
                this.parts = parts;
                this.lastEmitted = value;
                return;
            }
            this.loadValue(value);
        },

        editable(editable) {
            this.editor.setEditable(editable, false);
        },
    },

    created() {
        // je Editor eigener Debounce (in methods wäre er für alle Editoren gemeinsam und ohne cancel erreichbar)
        this.appendixInput = debounce(() => this.commitAppendix(), 300);
    },

    mounted() {
        const shadow = this.$refs.contentHost.attachShadow({mode: 'open'});
        const style = document.createElement('style');
        style.textContent = contentStyle;
        const container = document.createElement('div');
        container.className = 'email-editor-content';
        shadow.appendChild(style);
        shadow.appendChild(container);
        this.contentRoot = shadow;

        this.editor = new Editor({
            element: container,
            injectCSS: false,
            extensions: emailExtensions({placeholder: this.placeholder, onPasted: pasted => this.onPasted(pasted)}),
            editable: this.editable,
            onUpdate: () => this.emitValue(),
            onTransaction: event => this.onTransaction(event),
        });
        this.loadValue(this.value);
        document.addEventListener('mousedown', this.closePalette);
        document.addEventListener('selectionchange', this.onSelectionChange);
        window.addEventListener('mouseup', this.endMouseSelection);
    },

    beforeDestroy() {
        this.appendixInput.cancel();
        document.removeEventListener('mousedown', this.closePalette);
        document.removeEventListener('selectionchange', this.onSelectionChange);
        window.removeEventListener('mouseup', this.endMouseSelection);
        this.editor.destroy();
    },
};
</script>
